import { createSlice } from "@reduxjs/toolkit";
import {
  ISupplierResponsesAllState,
  ISortConfig,
} from "../types/supplier-responses-all";
import {
  getSpecificationSupplierProductsThunk,
  getSupplierResponsesAllThunk,
} from "../thunks/supplierResponsesAllThunk";
import { ISupplierResponsesAllItem } from "@/shared/services/interface/responses/ISupplierResponsesAllResponse";

const initialState: ISupplierResponsesAllState = {
  data: [],
  pagination: {
    offset: 0,
    limit: 20,
    totalCount: 0,
  },
  sort: {
    sortBy: null,
    sortOrder: null,
  },
  search: "",
  status: [],
  hiddenColumnFields: [], // No columns hidden by default
  loading: false,
  error: null,
  productsBySpecification: {},
};

export const supplierResponsesAllSlice = createSlice({
  name: "supplierResponsesAll",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSort: (state, action: { payload: Partial<ISortConfig> }) => {
      state.sort = { ...state.sort, ...action.payload };
      // Reset pagination when sort changes
      state.pagination.offset = 0;
    },
    setSearch: (state, action: { payload: string }) => {
      state.search = action.payload;
      // Reset pagination when search changes
      state.pagination.offset = 0;
    },
    setStatus: (state, action: { payload: string[] }) => {
      state.status = action.payload;
      // Reset pagination when status changes
      state.pagination.offset = 0;
    },
    toggleColumnVisibility: (state, action: { payload: string }) => {
      const field = action.payload;
      const index = state.hiddenColumnFields.indexOf(field);
      if (index > -1) {
        // Column is hidden, show it
        state.hiddenColumnFields.splice(index, 1);
      } else {
        // Column is visible, hide it
        state.hiddenColumnFields.push(field);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupplierResponsesAllThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSupplierResponsesAllThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.data = action.payload.data as ISupplierResponsesAllItem[];
          state.pagination = {
            offset: action.meta.arg.offset ?? 0,
            limit: action.meta.arg.limit ?? 20,
            totalCount: action.payload.totalCount,
          };
        }
      })
      .addCase(getSupplierResponsesAllThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        getSpecificationSupplierProductsThunk.pending,
        (state, action) => {
          const specId = action.meta.arg.specificationId;
          const existing = state.productsBySpecification[specId];
          state.productsBySpecification[specId] = {
            items: existing?.items || [],
            pagination: existing?.pagination || {
              offset: action.meta.arg.offset ?? 0,
              limit: action.meta.arg.limit ?? 10,
              totalCount: existing?.pagination?.totalCount || 0,
            },
            isLoading: true,
            error: null,
            initialized: existing?.initialized ?? false,
          };
        }
      )
      .addCase(
        getSpecificationSupplierProductsThunk.fulfilled,
        (state, action) => {
          const { specificationId, response } = action.payload;
          state.productsBySpecification[specificationId] = {
            items: response.products,
            pagination: {
              limit: response.limit,
              offset: response.offset,
              totalCount: response.totalCount,
            },
            isLoading: false,
            error: null,
            initialized: true,
          };
        }
      )
      .addCase(
        getSpecificationSupplierProductsThunk.rejected,
        (state, action) => {
          const specId = action.meta.arg.specificationId;
          const existing = state.productsBySpecification[specId];
          state.productsBySpecification[specId] = {
            items: existing?.items || [],
            pagination:
              existing?.pagination || {
                offset: action.meta.arg.offset ?? 0,
                limit: action.meta.arg.limit ?? 10,
                totalCount: existing?.pagination?.totalCount || 0,
              },
            isLoading: false,
            error:
              (action.payload as string) ||
              action.error.message ||
              "Bir hata oluştu",
            initialized: existing?.initialized ?? false,
          };
        }
      );
  },
});

export const { setLoading, clearError, setSort, setSearch, setStatus, toggleColumnVisibility } = supplierResponsesAllSlice.actions;

export default supplierResponsesAllSlice.reducer;

